import { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../lib/api';
import { AudioPlayer } from '../components/AudioPlayer';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';

export function Recordings() {
  const { t, language } = useLanguage();
  const { member } = useAuth();
  const queryClient = useQueryClient();

  const { data: recordingsData, isLoading } = useQuery({
    queryKey: ['familyRecordings'],
    queryFn: () => api.getFamilyRecordings(20)
  });

  const { data: historyData } = useQuery({
    queryKey: ['streakHistory'],
    queryFn: () => api.getHistory(30)
  });

  const deleteMutation = useMutation({
    mutationFn: (recordingId: string) => api.deleteRecording(recordingId),
    onSuccess: () => {
      // Refresh recordings and stats
      queryClient.invalidateQueries({ queryKey: ['familyRecordings'] });
      queryClient.invalidateQueries({ queryKey: ['myRecordings'] });
      queryClient.invalidateQueries({ queryKey: ['familyStats'] });
      queryClient.invalidateQueries({ queryKey: ['myStreak'] });
    }
  });

  const recordings = recordingsData?.recordings || [];
  const history = historyData?.history || [];
  const isParent = member?.is_parent === 1 || member?.is_parent === true;

  return (
    <div className="container py-8">
      <div className="mb-8">
        <h1 className="text-2xl mb-1">{t('myRecordings')}</h1>
        <p className="text-gray-600">{t('recordingsSubtitle')}</p>
      </div>

      <div className="grid grid-2 gap-8">
        {/* Recent Recordings */}
        <div>
          <h2 className="text-xl mb-4">{t('myRecordings')} 🎙️</h2>
          {isLoading ? (
            <div className="card">{t('loading')}</div>
          ) : recordings.length === 0 ? (
            <div className="card empty-state">
              <p className="empty-state-icon">🎙️</p>
              <p>{t('noRecordings')}</p>
              <p className="text-sm mt-2">{t('startRecording')}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {recordings.map((recording: any) => (
                <RecordingCard
                  key={recording.id}
                  recording={recording}
                  language={language}
                  canDelete={isParent || recording.member_id === member?.id}
                  onDelete={() => deleteMutation.mutate(recording.id)}
                  isDeleting={deleteMutation.isPending}
                />
              ))}
            </div>
          )}
        </div>

        {/* Activity History */}
        <div>
          <h2 className="text-xl mb-4">📅</h2>
          <div className="card">
            {history.length === 0 ? (
              <div className="empty-state">
                <p>{t('startRecording')}</p>
              </div>
            ) : (
              <div className="space-y-2">
                {history.map((day: any, index: number) => (
                  <HistoryItem key={index} day={day} language={language} />
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function RecordingCard({
  recording,
  language,
  canDelete,
  onDelete,
  isDeleting
}: {
  recording: any;
  language: string;
  canDelete: boolean;
  onDelete: () => void;
  isDeleting: boolean;
}) {
  const [showConfirm, setShowConfirm] = useState(false);
  const phraseContent = recording.phrase_content || recording.custom_phrase_content || 'Affirmation';
  const deleteText = language === 'es' ? 'Eliminar' : 'Delete';
  const confirmText = language === 'es' ? '¿Eliminar grabación?' : 'Delete recording?';
  const cancelText = language === 'es' ? 'Cancelar' : 'Cancel';

  const handleDelete = () => {
    onDelete();
    setShowConfirm(false);
  };

  return (
    <div className="card">
      <div className="flex items-start gap-4 mb-4">
        <div
          className="avatar"
          style={{ backgroundColor: recording.avatar_color || '#6366f1' }}
        >
          {recording.member_name?.charAt(0).toUpperCase()}
        </div>
        <div className="flex-1">
          <p className="font-semibold">{recording.member_name}</p>
          <p className="text-sm text-gray-500">
            {formatDate(recording.created_at, language)}
          </p>
        </div>
        {canDelete && (
          <div>
            {showConfirm ? (
              <div className="flex gap-2">
                <button
                  onClick={handleDelete}
                  disabled={isDeleting}
                  className="btn btn-danger"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                >
                  {isDeleting ? '...' : confirmText}
                </button>
                <button
                  onClick={() => setShowConfirm(false)}
                  className="btn btn-secondary"
                  style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                >
                  {cancelText}
                </button>
              </div>
            ) : (
              <button
                onClick={() => setShowConfirm(true)}
                className="btn btn-secondary"
                style={{ padding: '0.25rem 0.5rem', fontSize: '0.75rem' }}
                title={deleteText}
              >
                🗑️
              </button>
            )}
          </div>
        )}
      </div>
      <p className="text-gray-700 mb-4 italic">"{phraseContent}"</p>
      <AudioPlayer recordingId={recording.id} />
    </div>
  );
}

function HistoryItem({ day, language }: { day: any; language: string }) {
  const date = new Date(day.assigned_date);
  const isToday = day.assigned_date === new Date().toISOString().split('T')[0];
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  return (
    <div className={`flex items-center gap-4 p-3 rounded-lg ${day.completed ? 'bg-green-50' : 'bg-gray-50'}`}>
      <div className="text-center min-w-[50px]">
        <p className="text-xs text-gray-500 uppercase">
          {date.toLocaleDateString(locale, { weekday: 'short' })}
        </p>
        <p className="text-lg font-bold">
          {date.getDate()}
        </p>
      </div>
      <div className="flex-1">
        <p className={`text-sm ${day.completed ? 'text-green-700' : 'text-gray-600'}`}>
          {day.phrase_content ? `"${day.phrase_content.substring(0, 50)}..."` : ''}
        </p>
      </div>
      <div>
        {day.completed ? (
          <span className="text-green-500 text-xl">✓</span>
        ) : isToday ? (
          <span className="text-yellow-500 text-xl">◯</span>
        ) : (
          <span className="text-gray-300 text-xl">✗</span>
        )}
      </div>
    </div>
  );
}

function formatDate(dateString: string, language: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);
  const locale = language === 'es' ? 'es-ES' : 'en-US';

  if (diffMins < 1) return language === 'es' ? 'Ahora' : 'Just now';
  if (diffMins < 60) return language === 'es' ? `hace ${diffMins}m` : `${diffMins}m ago`;
  if (diffHours < 24) return language === 'es' ? `hace ${diffHours}h` : `${diffHours}h ago`;
  if (diffDays < 7) return language === 'es' ? `hace ${diffDays}d` : `${diffDays}d ago`;

  return date.toLocaleDateString(locale, {
    month: 'short',
    day: 'numeric'
  });
}
